/**
 * Turning the literals in a saved query into controls.
 *
 * A saved query like
 *
 *   WHERE trainer_attendance.attendance_timestamp >= '2026-07-01'
 *
 * is only useful for the month it was written in. Rather than make people
 * duplicate it per period, the literals are found, typed, and offered as
 * inputs — a date gets a date picker, a number a number field — and the query
 * is rewritten with whatever the reader chooses.
 *
 * ── On safety ────────────────────────────────────────────────────────────
 *
 * This is a convenience, not a security boundary, and it is important to be
 * clear about which. The rewritten statement is sent to the same endpoint as
 * anything typed into the console, so it passes the full SQL guard — lexer,
 * AST parse, policy, row and time limits, masking — before a row is read. It
 * also grants nothing new: every person who can see a dashboard already holds
 * `query:run` and could type the same statement by hand.
 *
 * What this module owes the user is therefore correctness rather than
 * containment: replace exactly the intended bytes, escape what it emits, and
 * refuse anything it cannot type confidently instead of guessing. Detection is
 * deliberately conservative — a literal it does not recognise is simply not
 * offered as a control, which costs a feature rather than breaking a query.
 */

export type ParameterKind = 'date' | 'datetime' | 'number' | 'text';

export interface SqlParameter {
  /** Stable within one statement: the literal's start offset. */
  id: string;
  /** Byte range of the literal in the original SQL, quotes included. */
  offset: number;
  length: number;
  kind: ParameterKind;
  /** Current value, without the surrounding quotes. */
  value: string;
  /** The column this literal is compared against, when one is evident. */
  column: string | null;
  /** `>=`, `<`, `LIKE`, `LIMIT` … — shown so two bounds read as a range. */
  operator: string | null;
  /** Human label for the control. */
  label: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/;
const NUMBER = /^-?\d+(\.\d+)?$/;

/** Operators after which a literal is worth offering as a control. */
const OPERATORS = ['>=', '<=', '<>', '!=', '=', '>', '<'];
const WORD_OPERATORS = ['LIKE', 'NOT LIKE', 'IN', 'BETWEEN', 'AND', 'LIMIT', 'OFFSET'];

interface RawLiteral {
  offset: number;
  length: number;
  /** Text without quotes. */
  value: string;
  quoted: boolean;
}

/**
 * Finds string and numeric literals, skipping comments and quoted identifiers.
 *
 * Mirrors the scanning rules of the server's lexer. It does not share that
 * code because the server's copy is the security-critical one and should not
 * grow a second caller with different needs — but the rules it implements
 * (backslash escapes, doubled quotes, `--` needing whitespace, `/*` blocks)
 * are the same, because getting them wrong here means rewriting the wrong
 * bytes.
 */
function scanLiterals(sql: string): RawLiteral[] {
  const found: RawLiteral[] = [];
  const n = sql.length;
  let i = 0;

  while (i < n) {
    const ch = sql[i]!;
    const next = sql[i + 1];

    // -- comment (MySQL requires whitespace after the dashes)
    if (ch === '-' && next === '-' && (sql[i + 2] === undefined || /\s/.test(sql[i + 2]!))) {
      const end = sql.indexOf('\n', i);
      i = end === -1 ? n : end;
      continue;
    }
    if (ch === '#') {
      const end = sql.indexOf('\n', i);
      i = end === -1 ? n : end;
      continue;
    }
    if (ch === '/' && next === '*') {
      const close = sql.indexOf('*/', i + 2);
      i = close === -1 ? n : close + 2;
      continue;
    }

    // Backticked and double-quoted text is an identifier on MySQL, not a
    // value — renaming a column is not something a dashboard control should
    // offer, so both are skipped rather than parameterised.
    if (ch === '`' || ch === '"') {
      i = skipQuoted(sql, i, ch);
      continue;
    }

    if (ch === "'") {
      const end = skipQuoted(sql, i, ch);
      found.push({
        offset: i,
        length: end - i,
        value: unescape(sql.slice(i + 1, end - 1)),
        quoted: true,
      });
      i = end;
      continue;
    }

    // A bare number, but only where one can start — otherwise the 2 in
    // `user2` or the digits of a date inside a literal would be picked up.
    if (/[0-9]/.test(ch) && !isIdentifierChar(sql[i - 1])) {
      let j = i;
      while (j < n && /[0-9.]/.test(sql[j]!)) j++;
      // A trailing identifier character means this was never a number.
      if (!isIdentifierChar(sql[j])) {
        found.push({ offset: i, length: j - i, value: sql.slice(i, j), quoted: false });
      }
      i = j;
      continue;
    }

    i++;
  }

  return found;
}

function isIdentifierChar(ch: string | undefined): boolean {
  return ch !== undefined && /[A-Za-z0-9_$]/.test(ch);
}

/** Returns the index just past the closing quote. */
function skipQuoted(sql: string, start: number, quote: string): number {
  let i = start + 1;
  while (i < sql.length) {
    const ch = sql[i]!;
    if (ch === '\\' && quote !== '`') {
      i += 2;
      continue;
    }
    if (ch === quote) {
      if (sql[i + 1] === quote) {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i++;
  }
  return sql.length;
}

function unescape(inner: string): string {
  return inner.replace(/\\(.)/g, '$1').replace(/''/g, "'");
}

function classify(value: string, quoted: boolean): ParameterKind {
  if (DATE.test(value)) return 'date';
  if (DATETIME.test(value)) return 'datetime';
  if (!quoted && NUMBER.test(value)) return 'number';
  return 'text';
}

/**
 * Looks backwards from a literal for the operator and column it belongs to.
 *
 * This is what makes the control legible: "attendance_timestamp ≥" rather than
 * "parameter 3". Returns nulls when the context is not recognisable, and the
 * caller drops the literal rather than showing something unexplained.
 */
function contextBefore(sql: string, offset: number): { operator: string | null; column: string | null } {
  const before = sql.slice(0, offset).replace(/\s+$/, '');
  const upper = before.toUpperCase();

  for (const op of OPERATORS) {
    if (before.endsWith(op)) {
      const lhs = before.slice(0, before.length - op.length).replace(/\s+$/, '');
      return { operator: op, column: identifierAtEnd(lhs) };
    }
  }

  for (const word of WORD_OPERATORS) {
    if (upper.endsWith(word) && !isIdentifierChar(before[before.length - word.length - 1])) {
      const lhs = before.slice(0, before.length - word.length).replace(/\s+$/, '');
      // BETWEEN x AND y: the second bound's column is the one before BETWEEN.
      const column = word === 'AND' ? identifierBeforeBetween(before) : identifierAtEnd(lhs);
      return { operator: word, column };
    }
  }

  // A comma continues a list: `IN ('a', 'b')`.
  if (before.endsWith(',')) return { operator: ',', column: null };

  return { operator: null, column: null };
}

function identifierAtEnd(text: string): string | null {
  const m = /([A-Za-z_$][A-Za-z0-9_$]*(?:\.[A-Za-z_$][A-Za-z0-9_$]*)*)$/.exec(text);
  return m ? m[1]! : null;
}

function identifierBeforeBetween(before: string): string | null {
  const idx = before.toUpperCase().lastIndexOf('BETWEEN');
  if (idx === -1) return null;
  return identifierAtEnd(before.slice(0, idx).replace(/\s+$/, ''));
}

/** Trims a qualified name down to what a person would call the column. */
function shortColumn(column: string | null): string | null {
  if (!column) return null;
  const parts = column.split('.');
  return parts[parts.length - 1] ?? column;
}

const OPERATOR_WORDS: Record<string, string> = {
  '>=': 'from',
  '>': 'after',
  '<=': 'up to',
  '<': 'before',
  '=': 'is',
  '<>': 'is not',
  '!=': 'is not',
  LIKE: 'matches',
  'NOT LIKE': 'does not match',
  IN: 'is one of',
  BETWEEN: 'from',
  AND: 'to',
  LIMIT: 'row limit',
  OFFSET: 'skip',
};

function labelFor(column: string | null, operator: string | null, kind: ParameterKind): string {
  const word = operator ? OPERATOR_WORDS[operator] ?? operator.toLowerCase() : null;
  const name = shortColumn(column)?.replace(/_/g, ' ');

  if (operator === 'LIMIT' || operator === 'OFFSET') return word!;
  if (name && word) return `${name} — ${word}`;
  if (name) return name;
  if (word) return word;
  return kind;
}

/**
 * The literals worth offering as controls.
 *
 * Only those in a recognisable comparison, list or limit: a literal buried in
 * `DATE_FORMAT(x, '%Y-%m')` is a piece of the query's logic, not an input, and
 * turning it into a text box invites someone to break their own dashboard.
 */
export function detectParameters(sql: string): SqlParameter[] {
  const params: SqlParameter[] = [];

  for (const literal of scanLiterals(sql)) {
    const { operator, column } = contextBefore(sql, literal.offset);
    if (!operator) continue;
    // A bare comma with no column context is a list member; keep it only when
    // the list itself was identifiable.
    if (operator === ',' && !column) continue;

    const kind = classify(literal.value, literal.quoted);

    // `LIMIT` and `OFFSET` are numbers by definition; anything else there is
    // not something to offer.
    if ((operator === 'LIMIT' || operator === 'OFFSET') && kind !== 'number') continue;

    params.push({
      id: `p${literal.offset}`,
      offset: literal.offset,
      length: literal.length,
      kind,
      value: literal.value,
      column,
      operator,
      label: labelFor(column, operator, kind),
    });
  }

  return params;
}

export class ParameterError extends Error {}

/** Emits a literal that means exactly the given value, or throws. */
function toLiteral(kind: ParameterKind, value: string): string {
  const trimmed = value.trim();

  switch (kind) {
    case 'date':
      if (!DATE.test(trimmed)) throw new ParameterError(`"${value}" is not a date (YYYY-MM-DD).`);
      return `'${trimmed}'`;

    case 'datetime':
      if (!DATETIME.test(trimmed)) {
        // A date alone is a reasonable thing to type into a datetime field.
        if (DATE.test(trimmed)) return `'${trimmed} 00:00:00'`;
        throw new ParameterError(`"${value}" is not a date and time.`);
      }
      return `'${trimmed.replace('T', ' ')}'`;

    case 'number':
      if (!NUMBER.test(trimmed)) throw new ParameterError(`"${value}" is not a number.`);
      return trimmed;

    case 'text':
      if (trimmed.length > 512) throw new ParameterError('That value is too long.');
      // Escape the two characters that can end or continue a MySQL string.
      return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "''")}'`;
  }
}

/**
 * Rewrites the statement with new values.
 *
 * Replacements run back-to-front so that each offset still refers to the
 * original text when it is used — patching forwards would shift every
 * subsequent literal by the length change of the one before it.
 */
export function applyParameters(
  sql: string,
  parameters: SqlParameter[],
  values: Record<string, string>,
): string {
  const ordered = [...parameters].sort((a, b) => b.offset - a.offset);
  let out = sql;

  for (const parameter of ordered) {
    const next = values[parameter.id];
    if (next === undefined || next === parameter.value) continue;
    const literal = toLiteral(parameter.kind, next);
    out = out.slice(0, parameter.offset) + literal + out.slice(parameter.offset + parameter.length);
  }

  return out;
}
